import { Component, ChangeDetectionStrategy, ChangeDetectorRef, EventEmitter, HostListener, inject, Input, Output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IParametreSetiGrubu, IParametreSeti } from '@/src/service/fikoyt-ui/interfaces/IParametreSeti.interface';
import { IParametre } from '@/src/service/fikoyt-ui/interfaces/IParametre/IParametre';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzInputModule } from 'ng-zorro-antd/input';
import { turkceKucuk } from '@/src/app/shared/utils/regex.utils';

@Component({
  selector: 'parametre-seti-secici',
  standalone: true,
  imports: [CommonModule, FormsModule, NzTooltipModule, NzInputModule],
  templateUrl: './parametre-seti-secici.component.html',
  styleUrls: ['./parametre-seti-secici.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParametreSetiSeciciComponent {
  private cd = inject(ChangeDetectorRef);
  @Input() setler: IParametreSetiGrubu[] = [];
  @Input() kullaniciYetkileri: string[] = [];

  private _seciliSetId: string | null = null;
  private _secimKaldirmaDanGeldi = false;
  @Input() set seciliSetId(v: string | null) {
    const onceki = this._seciliSetId;
    this._seciliSetId = v;
    if (v == null && onceki != null && !this._secimKaldirmaDanGeldi) {
      this.seciliSet.set(null);
      this.navigasyonYolu.set([]);
      this.aktifAltGruplar.set(null);
    }
    this._secimKaldirmaDanGeldi = false;
  }
  get seciliSetId() { return this._seciliSetId; }

  @Output() panelKapat = new EventEmitter<void>();
  @Output() setSecildi = new EventEmitter<{
    setId: string;
    parametreler: IParametre[];
    sayfaKurallari?: IParametreSeti['sayfaKurallari'];
    bilesen?: string;
    bilesenGorunum?: string;
  }>();
  @Output() setKaldirildi = new EventEmitter<void>();
  @Output() parametrelerDegisti = new EventEmitter<IParametre[]>();

  readonly navigasyonYolu = signal<IParametreSetiGrubu[]>([]);
  readonly aktifAltGruplar = signal<IParametreSeti[] | null>(null);
  readonly seciliSet = signal<IParametreSeti | null>(null);
  readonly aramaMetni = signal('');
  animSinifi = '';

  readonly breadcrumb = computed(() =>
    this.navigasyonYolu().map(g => g.baslik)
  );

  private turkceKucukYerel(str: string): string {
    return turkceKucuk(str);
  }

  private aramaEslesiyor(grup: { baslik?: string; aciklama?: string; simge?: string }, sorgu: string): boolean {
    if (!sorgu) return true;
    const q = this.turkceKucukYerel(sorgu);
    return this.turkceKucukYerel(grup.baslik || '').includes(q)
        || this.turkceKucukYerel(grup.aciklama || '').includes(q)
        || (grup.simge || '').includes(q);
  }

  private derinAra(grup: IParametreSetiGrubu, sorgu: string): boolean {
    if (this.aramaEslesiyor(grup, sorgu)) return true;
    if (grup.altGruplar?.some(alt => this.aramaEslesiyor(alt, sorgu))) return true;
    return false;
  }

  readonly gosterilecekGruplar = computed(() => {
    if (this.aktifAltGruplar()) return null;
    const sorgu = this.aramaMetni();
    return this.setler
      .filter(g => this.gorunurMu(g.yetkiler))
      .filter(g => this.derinAra(g, sorgu));
  });

  readonly filtrelenmisAltGruplar = computed(() => {
    const altler = this.aktifAltGruplar();
    if (!altler) return null;
    const sorgu = this.aramaMetni();
    if (!sorgu) return altler;
    return altler.filter(s => this.aramaEslesiyor(s, sorgu));
  });

  gorunurMu(yetkiler?: { gorebilir?: string[]; kullanabilir?: string[] }): boolean {
    if (!yetkiler?.gorebilir?.length) return true;
    return yetkiler.gorebilir.some(y => this.kullaniciYetkileri.includes(y));
  }

  kullanilabilirMi(yetkiler?: { gorebilir?: string[]; kullanabilir?: string[] }): boolean {
    if (!yetkiler?.kullanabilir?.length) return true;
    return yetkiler.kullanabilir.some(y => this.kullaniciYetkileri.includes(y));
  }

  @HostListener('window:popstate')
  onPopState() {
    if (this.navigasyonYolu().length > 0) {
      this.geriGit();
    }
  }

  grupTiklandi(grup: IParametreSetiGrubu) {
    if (!this.kullanilabilirMi(grup.yetkiler)) return;
    if (grup.bilesen) {
      this.seciliSet.set({ id: grup.id, baslik: grup.baslik, simge: grup.simge, parametreler: grup.parametreler ?? [] });
      this.setSecildi.emit({
        setId: grup.id,
        parametreler: grup.parametreler ? JSON.parse(JSON.stringify(grup.parametreler)) : [],
        sayfaKurallari: grup.sayfaKurallari,
        bilesen: grup.bilesen,
        bilesenGorunum: grup.bilesenGorunum,
      });
      return;
    }
    if (grup.altGruplar?.length) {
      history.pushState({ paylasim: 'parametreSet' }, '');
      this.animSinifi = 'sayfa-cikis-sol';
      setTimeout(() => {
        this.navigasyonYolu.update(y => [...y, grup]);
        this.aktifAltGruplar.set(grup.altGruplar!.filter(s => this.gorunurMu(s.yetkiler)));
        this.animSinifi = 'sayfa-giris-sag';
        setTimeout(() => this.animSinifi = '', 300);
      }, 180);
    } else if (grup.parametreler?.length) {
      this.setSecimYap({
        id: grup.id,
        baslik: grup.baslik,
        simge: grup.simge,
        parametreler: grup.parametreler,
        yetkiler: grup.yetkiler,
        sayfaKurallari: grup.sayfaKurallari,
      });
    }
  }

  altSetTiklandi(set: IParametreSeti) {
    if (!this.kullanilabilirMi(set.yetkiler)) return;
    if ((set as any).bilesen) {
      this.seciliSet.set({ id: set.id, baslik: set.baslik, simge: set.simge, parametreler: set.parametreler ?? [] });
      this.setSecildi.emit({
        setId: set.id,
        parametreler: set.parametreler ? JSON.parse(JSON.stringify(set.parametreler)) : [],
        sayfaKurallari: (set as any).sayfaKurallari,
        bilesen: (set as any).bilesen,
        bilesenGorunum: (set as any).bilesenGorunum,
      });
      return;
    }
    this.setSecimYap(set);
  }

  geriGit() {
    this.animSinifi = 'sayfa-cikis-sag';
    this.cd.markForCheck();
    setTimeout(() => {
      this.navigasyonYolu.update(y => y.slice(0, -1));
      const ust = this.navigasyonYolu();
      if (ust.length > 0) {
        const sonGrup = ust[ust.length - 1];
        this.aktifAltGruplar.set(sonGrup.altGruplar?.filter(s => this.gorunurMu(s.yetkiler)) ?? null);
      } else {
        this.aktifAltGruplar.set(null);
      }
      this.animSinifi = 'sayfa-giris-sol';
      this.cd.detectChanges();
      setTimeout(() => { this.animSinifi = ''; this.cd.markForCheck(); }, 300);
    }, 180);
  }

  private setSecimYap(set: IParametreSeti) {
    const parametreler: IParametre[] = JSON.parse(JSON.stringify(set.parametreler)).map((p: IParametre) => ({
      ...p,
      degeri: p.degeri ?? null,
    }));
    this.seciliSet.set(set);
    this.setSecildi.emit({
      setId: set.id,
      parametreler,
      sayfaKurallari: set.sayfaKurallari,
    });
  }

  secimKaldir() {
    this._secimKaldirmaDanGeldi = true;
    this.seciliSet.set(null);
    this.setKaldirildi.emit();
  }

  secimSifirla() {
    this._secimKaldirmaDanGeldi = true;
    this.seciliSet.set(null);
    this.cd.detectChanges();
  }

  onParametrelerDegisti(parametreler: IParametre[]) {
    this.parametrelerDegisti.emit(parametreler);
  }
}
